import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import './StickyCardStack.css';

/**
 * One card in the stack. It is sticky, so it pins at the top of the viewport
 * and stays there while the next card scrolls up over it, shrinking as it goes
 * — which is what leaves the fanned edges of the earlier cards on screen.
 */
function StickyCard({ index, progress, range, targetScale, children }) {
  const scale = useTransform(progress, range, [1, targetScale]);

  return (
    <div className="dp-stack-slot">
      <motion.div
        className="dp-stack-card"
        style={{ scale, top: `calc(-5vh + ${index * 22 + 120}px)` }}
      >
        {children}
      </motion.div>
    </div>
  );
}

/**
 * Scroll-driven stack of cards.
 *
 * `items` are rendered through `children` as a render prop, so the stack does
 * not care what a card contains.
 */
export default function StickyCardStack({ items, children }) {
  const container = useRef(null);
  const { scrollYProgress } = useScroll({
    target: container,
    offset: ['start start', 'end end'],
  });

  const count = items.length;

  return (
    <div ref={container} className="dp-stack">
      {items.map((item, i) => (
        <StickyCard
          key={item.slug || item.id || i}
          index={i}
          progress={scrollYProgress}
          // Each card starts shrinking when the one after it begins its run.
          range={[count > 1 ? i / (count - 1) : 0, 1]}
          // The last card stays full size; every card before it ends a step smaller.
          targetScale={Math.max(0.5, 1 - (count - i - 1) * 0.1)}
        >
          {children(item, i)}
        </StickyCard>
      ))}
    </div>
  );
}

/**
 * Adapted from Skiper 16 · StickyCard_001 by @gurvinder-singh02 (Skiper UI,
 * https://gxuri.me) — free to use with attribution. The scroll mechanic, the
 * per-card scale ranges and the stagger are kept as published; the card
 * contents and sizing are ours.
 */
